import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import {
  Bell,
  Building2,
  Award,
  UserCheck,
  MessageSquare,
  FileText,
  Mail,
  User,
  Shield,
  Clock,
  X,
  ChevronRight,
  AlertCircle
} from 'lucide-react';

// Map icon names to components
const IconMap = {
  Building2,
  Award,
  UserCheck,
  MessageSquare,
  FileText,
  Mail,
  User,
  Shield,
  Clock,
  AlertCircle
};

const NotificationBell = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasHighPriority, setHasHighPriority] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  
  const primaryColor = theme?.primary_color || '#2B4B6A';

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!token) return;
    
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setTotalCount(res.data.total_count || 0);
      setHasHighPriority(res.data.has_high_priority || false);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Refresh notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500 bg-red-50';
      case 'medium': return 'text-orange-500 bg-orange-50';
      case 'low': return 'text-blue-500 bg-blue-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  // Get icon component
  const getIcon = (iconName) => {
    const Icon = IconMap[iconName] || AlertCircle;
    return Icon;
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{ '--tw-ring-color': primaryColor }}
        data-testid="notification-bell"
        aria-label={`Notifications ${totalCount > 0 ? `(${totalCount} pending)` : ''}`}
      >
        <div className={`${hasHighPriority && totalCount > 0 ? 'animate-wiggle' : ''}`}>
          <Bell 
            className={`w-5 h-5 ${totalCount > 0 ? 'text-gray-700' : 'text-gray-400'}`}
            style={totalCount > 0 ? { color: primaryColor } : {}}
          />
        </div>
        
        {/* Notification Badge */}
        {totalCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white rounded-full"
            style={{ backgroundColor: hasHighPriority ? '#EF4444' : primaryColor }}
            data-testid="notification-count"
          >
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Action Items</h3>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">All caught up!</p>
                <p className="text-sm text-gray-400 mt-1">No pending actions</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {notifications.map((notification) => {
                  const Icon = getIcon(notification.icon);
                  const priorityClass = getPriorityColor(notification.priority);
                  
                  return (
                    <Link
                      key={notification.id}
                      to={notification.link}
                      onClick={() => setIsOpen(false)}
                      className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors group"
                      data-testid={`notification-${notification.id}`}
                    >
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${priorityClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {notification.title}
                          </p>
                          {notification.count > 1 && (
                            <span 
                              className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded-full text-white"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {notification.count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5 truncate">
                          {notification.message}
                        </p>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0 mt-1" />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                {totalCount} action{totalCount !== 1 ? 's' : ''} need your attention
              </p>
            </div>
          )}
        </div>
      )}

      {/* CSS for bell animation */}
      <style jsx="true">{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          50% { transform: rotate(10deg); }
          75% { transform: rotate(-5deg); }
        }
        .animate-wiggle {
          animation: wiggle 0.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
